import type React from 'react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { TONE_FILL, type OverviewTone } from '../overviewMeta';

export type ThresholdSegment = {
    tone: OverviewTone;
    /** Share of the bar's width, 0–1. The segments should sum to 1. */
    ratio: number;
};

type ThresholdBarProps = {
    /** Literal testid prefix, e.g. `cash-runway-scale`. */
    slot: string;
    segments: ThresholdSegment[];
    /** Where the reading falls, 0–1, or `null` when it falls nowhere yet. */
    marker: number | null;
    /** Tick labels spread across the bar, first left-aligned, last right. */
    ticks?: string[];
    /** Accessible name — this is the reading, not decoration. */
    ariaLabel: string;
};

/**
 * A banded scale with a marker: red / amber / green stretches of the range, and
 * a tick showing where the business actually sits.
 *
 * A scale rather than a fill. `Meter` and `ProgressBar` both say "this much of
 * that" — 169 out of 180 — which is the wrong sentence. What matters is *which
 * band* the number lands in, and where the band edges are, so the reader can
 * see 47 days is nearly the amber/green line without being told. No HeroUI
 * component draws that, so this is plain markup, like the zone sparklines.
 */
export const ThresholdBar: React.FC<ThresholdBarProps> = ({
    slot,
    segments,
    marker,
    ticks,
    ariaLabel,
}) => (
    <div className="flex flex-col gap-1" {...getNodeTestIdProp(slot)}>
        <div
            aria-label={ariaLabel}
            className="relative flex h-2.5 w-full overflow-hidden rounded-full"
            role="img"
        >
            {segments.map((segment, index) => (
                <div
                    className={TONE_FILL[segment.tone]}
                    key={`${segment.tone}-${index}`}
                    style={{ width: `${segment.ratio * 100}%` }}
                />
            ))}
            {marker === null ? null : (
                <div
                    className="absolute top-0 h-full w-0.5 -translate-x-1/2 bg-foreground"
                    style={{ left: `${marker * 100}%` }}
                    {...getNodeTestIdProp(`${slot}-marker`)}
                />
            )}
        </div>
        {ticks ? (
            <div className="flex justify-between text-xs text-muted">
                {ticks.map((tick) => (
                    <span key={tick}>{tick}</span>
                ))}
            </div>
        ) : null}
    </div>
);
